import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { UsersIcon } from '@heroicons/react/24/outline';
import { PageLayout } from '../components/ui/PageLayout';
import { Benachrichtigung } from '../components/ui/Benachrichtigung';
import { Filterleiste } from '../components/ui/Filterleiste';
import { Rueckfrage } from '../components/ui/Rueckfrage';
import { SeitenblaetterFuss } from '../components/ui/SeitenblaetterFuss';
import { filterAuswahl, seitenwechsel } from '../components/ui/filterZustand';
import { useBenachrichtigung } from '../hooks/useBenachrichtigung';
import { useRueckfrage } from '../hooks/useRueckfrage';
import { datumKurz } from '../utils/datum';
import { ROLLEN_ANZEIGE, User, UserFilters, UserRole, Pagination } from '../types';
import { userService, UserListItem } from '../services/userService';
import { getErrorMessage } from '../utils/errors';
import { useAuth } from '../contexts/authContextValue';

// Auswahl-Reihenfolge der Rollen — bewusst aus `ROLLEN_ANZEIGE` statt aus einer zweiten Liste hier
// oder aus `Object.values(UserRole)`. Das Enum ist nach Prisma sortiert (Administrator, Premium,
// Free, Moderator, Redaktion) und mischt damit Mitarbeiter-Rollen und Zugriffsstufen; die
// Anzeige-Karte steht in der Reihenfolge, die im Dropdown sinnvoll ist. Der Cast ist sicher, weil
// `satisfies Record<UserRole, string>` die Schlüssel exakt auf das Enum festnagelt.
const ROLLEN = Object.keys(ROLLEN_ANZEIGE) as UserRole[];

/** Die Auswahllisten der Filterleiste als Optionen-Paare. */
const ROLLEN_OPTIONEN = ROLLEN.map((rolle) => ({ wert: rolle, text: ROLLEN_ANZEIGE[rolle] }));

/**
 * ⚠️ Die Annotation ist der Punkt, nicht Beiwerk: sie nagelt die Optionswerte an `UserFilters`
 * fest. Ohne sie wären es zwei beliebige Zeichenketten, und ein Tippfehler („aktive") ginge als
 * Filterwert ans Backend, wo `isActive = (status === 'active' || status === 'true')` ihn stumm als
 * „inaktiv" läse. Diese Liste ist auch der Grund, warum ihr „Status" NICHT mit dem der
 * Medien-Seiten zusammengelegt ist: dort heißt Status die Veröffentlichungs-Stufe, hier der
 * Konto-Zustand.
 */
const STATUS_OPTIONEN: ReadonlyArray<{ wert: NonNullable<UserFilters['status']>; text: string }> = [
  { wert: 'active', text: 'Aktiv' },
  { wert: 'inactive', text: 'Inaktiv' },
];

const anzeigeName = (u: UserListItem): string =>
  [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || u.email;

/** Wartezeit nach dem letzten Tastenanschlag, bevor die Suche wirklich losgeschickt wird. */
const SUCH_ENTPRELLUNG_MS = 300;

/**
 * Ein abgebrochener Request ist KEIN Fehler, sondern das erwartete Ende einer überholten Anfrage —
 * er darf niemals als Meldung in der Oberfläche landen. Sonst blitzt beim Tippen bei jedem
 * Anschlag „canceled" auf.
 *
 * Zwei Prüfungen, weil beide Formen vorkommen: `axios.isCancel` erkennt den `CanceledError`, den
 * axios beim Abbruch über `signal` selbst wirft; die Namensprüfung fängt zusätzlich den rohen
 * `AbortError` aus der darunterliegenden XHR-/Fetch-Ebene ab, falls der ungefiltert durchkommt.
 */
const istAbbruch = (fehler: unknown): boolean => {
  if (axios.isCancel(fehler)) return true;
  const name = (fehler as { name?: unknown } | null | undefined)?.name;
  return name === 'CanceledError' || name === 'AbortError';
};

/**
 * Erkennt die eigene Zeile. Das ist die sicherheitsrelevante Stelle der Seite: nur was hier `true`
 * ergibt, wird gesperrt und mit „(Sie)" markiert.
 *
 * Der ID-Vergleich trägt heute, und zwar belegbar statt behauptet: `GET /api/users/me` antwortet
 * mit `req.user.id` (UserController.getCurrentUser), und `req.user` ist die Prisma-Zeile, die
 * `AuthMiddleware.authenticate` per `db.user.findUnique({ where: { supabaseUid } })` auflöst —
 * also dieselbe Zeile und dieselbe Spalte `User.id` (cuid), aus der auch
 * `BaseUserQueryService.getUserSelect()` die Liste bedient.
 *
 * ⚠️ Genau deshalb steht der E-Mail-Vergleich daneben. Im selben Datensatz liegt mit `supabaseUid`
 * eine ZWEITE, gleich aussehende Kennung (`String @unique`). Liefert `/me` eines Tages `payload.sub`
 * statt der Datenbank-ID, sind beide Werte weiterhin `string`: der Compiler schweigt, kein Test
 * würde rot, und der Selbstschutz fiele STILL weg — die Administratorin könnte sich selbst
 * herabstufen oder abschalten und sich damit aus genau der Seite aussperren, die den Schritt
 * zurücknimmt. Die E-Mail ist der zweite, unabhängige Schlüssel gegen dieses stille Versagen:
 * `User.email` ist in Prisma `@unique`, steht in `getUserSelect()` UND in der `/me`-Antwort.
 * Festgenagelt in `UserManagement.test.tsx` („greift auch, wenn /me eine fremde ID-Form liefert").
 */
const istEigeneZeile = (angemeldet: User | null, u: UserListItem): boolean => {
  if (!angemeldet) return false;
  if (angemeldet.id === u.id) return true;
  return Boolean(angemeldet.email) && angemeldet.email.toLowerCase() === u.email.toLowerCase();
};

const UserManagement: React.FC = () => {
  const { user: angemeldet } = useAuth();

  const [users, setUsers] = useState<UserListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Trennt Erst-Ladung von Hintergrund-Aktualisierung. Ohne diese zweite Größe ersetzt jedes
  // Neuladen nach einem Schreibvorgang die ganze Tabelle durch den Spinner — der gerade bediente
  // Knopf verschwindet aus dem DOM und der Tastatur-Fokus fällt auf `<body>`.
  const [hatGeladen, setHatGeladen] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Was im Suchfeld steht, ist NICHT sofort der Filter: der Wert wandert erst nach
  // `SUCH_ENTPRELLUNG_MS` ohne Anschlag in `filters` — siehe Entprellungs-Effekt unten.
  const [sucheEingabe, setSucheEingabe] = useState('');
  const [filters, setFilters] = useState<UserFilters>({
    page: 1,
    limit: 20,
    search: '',
    role: undefined,
    status: undefined,
  });
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  // Zustand, Zeitmessung und Aufräumen des Balkens stecken im Haken — samt der Regel, dass NUR der
  // Erfolg verblasst und der Fehler stehen bleibt. Begründung in `Benachrichtigung.tsx`.
  const { inhalt: benachrichtigung, melde, schließe } = useBenachrichtigung();
  // ⚠️ Das Feld heißt `rückfrage` und nicht `inhalt` — `inhalt` ist eine Zeile höher schon vergeben.
  // Genau dafür ist der Name im Haken so gewählt; Begründung in `useRueckfrage.ts`.
  const { rückfrage, frage, antworte } = useRueckfrage();
  // Welche Zeile gerade schreibt — sperrt genau diese Zeile, nicht die ganze Tabelle.
  const [busyId, setBusyId] = useState<string | null>(null);

  // Entprellung des Suchfelds: getippt wird in `sucheEingabe`, gesucht wird erst, wenn 300 ms lang
  // kein Anschlag mehr kam. Vorher löste jeder einzelne Buchstabe eine Anfrage aus.
  // `prev` unverändert zurückzugeben, wenn sich nichts geändert hat, ist kein Feinschliff, sondern
  // Pflicht: sonst erzeugt schon der erste Lauf dieses Effekts ein neues `filters`-Objekt und damit
  // eine zweite, überflüssige Anfrage direkt nach dem Aufbau der Seite.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => (prev.search === sucheEingabe ? prev : { ...prev, search: sucheEingabe, page: 1 }));
    }, SUCH_ENTPRELLUNG_MS);
    return () => clearTimeout(timer);
  }, [sucheEingabe]);

  // Genau EINE Listen-Anfrage darf gleichzeitig laufen. Jede neue bricht die vorige ab — sonst
  // gewinnt beim Tippen die zuletzt EINGETROFFENE statt der zuletzt GESTELLTEN Antwort, und in der
  // Tabelle steht das Ergebnis zu „to", während im Feld „tom" steht.
  const laufendeAnfrage = useRef<AbortController | null>(null);

  const ladeBenutzer = useCallback(async () => {
    laufendeAnfrage.current?.abort();
    const controller = new AbortController();
    laufendeAnfrage.current = controller;

    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await userService.getUsers(filters, controller.signal);
      // Zweite Sicherung neben dem Abbruch selbst: war die Antwort schon unterwegs, als abgebrochen
      // wurde, kommt sie trotzdem regulär an. Ohne diese Zeile schriebe sie sich über das neuere
      // Ergebnis.
      if (controller.signal.aborted) return;
      setUsers(response.data);
      setPagination(response.pagination);
      // Sackgasse beim Blättern an der Wurzel gelöst: wer den letzten Eintrag von Seite 2
      // deaktiviert oder wegfiltert, stünde sonst auf einer leeren Seite 2 — ohne Blätter-Leiste
      // (die hängt an der Liste) und damit ohne Weg zurück. Der Rücksprung kann nicht schleifen,
      // weil er auf Seite 1 nicht mehr greift.
      if (response.data.length === 0 && (filters.page ?? 1) > 1) {
        setFilters(prev => ({ ...prev, page: Math.max(1, (prev.page ?? 1) - 1) }));
      }
    } catch (error) {
      if (istAbbruch(error) || controller.signal.aborted) return;
      setUsers([]);
      // ⚠️ Eigener Fehlerzustand statt nur einer Meldung: der häufigste Fehler hier ist 403
      // (kein Administrator), und der verschwindet nach drei Sekunden — zurück bliebe eine leere
      // Liste, die wie „keine Benutzer" aussieht.
      setLoadError(getErrorMessage(error, 'Benutzer konnten nicht geladen werden.'));
    } finally {
      // Eine abgebrochene Anfrage räumt die Ladezustände NICHT auf: dafür ist die Anfrage
      // zuständig, die sie überholt hat und noch läuft.
      if (!controller.signal.aborted) {
        setIsLoading(false);
        setHatGeladen(true);
      }
    }
  }, [filters]);

  // Steht bewusst NACH der memoisierten Funktion (temporale Todeszone) — wie in den Nachbarseiten.
  // Der Cleanup bricht die noch laufende Anfrage ab, sobald sich die Filter ändern oder die Seite
  // verlassen wird.
  useEffect(() => {
    void ladeBenutzer();
    return () => laufendeAnfrage.current?.abort();
  }, [ladeBenutzer]);

  const handleFilterChange = <K extends keyof UserFilters>(key: K, value: UserFilters[K]) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  // Nach jedem Schreiben neu laden statt die Zeile lokal zu flicken: die Antwort trägt nur einen
  // Ausschnitt des Datensatzes, und die geänderte Zeile kann aus dem aktiven Filter herausfallen
  // (Rolle „Administrator" gefiltert, Rolle weggenommen). Ein lokaler Flick zeigte sie weiter.
  const handleRoleChange = async (u: UserListItem, role: UserRole) => {
    if (role === u.role) return;
    setBusyId(u.id);
    try {
      await userService.updateRole(u.id, role);
      melde('success', `${anzeigeName(u)} ist jetzt ${ROLLEN_ANZEIGE[role]}.`);
      await ladeBenutzer();
    } catch (error) {
      melde('error', getErrorMessage(error, 'Rollenwechsel fehlgeschlagen.'));
    } finally {
      setBusyId(null);
    }
  };

  // ⚠️ Gefragt wird NUR beim Abschalten, nicht beim Einschalten: „aktivieren" nimmt niemandem
  // etwas weg. Eine Frage vor jedem Klick wäre die Art Rückfrage, die man wegklickt, ohne sie zu
  // lesen — und damit auch die eine, auf die es ankommt.
  //
  // Was der Text behauptet, ist am Backend nachgemessen, nicht angenommen:
  //   • `AuthMiddleware.authenticate` löst den Datensatz bei JEDER Anfrage neu auf und antwortet
  //     bei `!user.isActive` mit 401 `USER_NOT_FOUND`. Der Ausschluss trifft also nicht erst die
  //     nächste Anmeldung, sondern die laufende Sitzung beim nächsten Aufruf — das gehört in den
  //     Text, sonst klingt „deaktivieren" nach „ab morgen".
  //   • Der Datensatz selbst bleibt stehen: `UserService.updateUserStatus` setzt ausschließlich
  //     `isActive`, es wird nichts gelöscht. Deshalb „die Inhalte bleiben unberührt".
  //   • Zurücknehmbar ist es ebenfalls belegbar — derselbe Knopf schaltet wieder ein, und die
  //     eigene Zeile ist ohnehin gesperrt (`istEigeneZeile`), man kann sich damit nicht selbst
  //     aussperren.
  const handleStatusToggle = async (u: UserListItem) => {
    const neuAktiv = !u.isActive;
    if (
      !neuAktiv &&
      !(await frage({
        titel: `${anzeigeName(u)} deaktivieren?`,
        text:
          'Anmeldung und API-Zugriff werden sofort abgewiesen — auch eine laufende Sitzung, ' +
          'denn der Konto-Zustand wird bei jeder Anfrage neu geprüft.\n\n' +
          'Die Inhalte des Kontos bleiben unberührt, und der Schritt lässt sich hier jederzeit ' +
          'zurücknehmen.',
        // ⚠️ Ohne eigene Beschriftung stünde hier „Löschen" (die Vorgabe bei `gefahr`) — und
        // gelöscht wird gerade NICHT. Ein Knopf, der mehr androht, als die Aktion tut, ist
        // dasselbe Problem wie einer, der zu wenig androht.
        bestätigenText: 'Deaktivieren',
        gefahr: true,
      }))
    ) {
      return;
    }
    setBusyId(u.id);
    try {
      await userService.updateStatus(u.id, neuAktiv);
      melde('success', `${anzeigeName(u)} ${neuAktiv ? 'aktiviert' : 'deaktiviert'}.`);
      await ladeBenutzer();
    } catch (error) {
      melde('error', getErrorMessage(error, 'Statuswechsel fehlgeschlagen.'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <PageLayout
      title="Benutzer-Verwaltung"
      subtitle="Rollen vergeben und Konten aktivieren oder deaktivieren"
    >
      <Benachrichtigung inhalt={benachrichtigung} onSchließen={schließe} />

      {/* Die Rückfrage vor dem Deaktivieren. Wo sie im Baum steht, ist für die Darstellung
          gleichgültig — `ModalSchicht` hängt sie per Portal an `document.body`; sie steht hier,
          weil sie zu dieser Seite gehört und mit ihr abgebaut werden soll (der Abbau verneint eine
          noch offene Frage, siehe `useRueckfrage`). */}
      <Rueckfrage rückfrage={rückfrage} onAntwort={antworte} />

      {/* Filter — die `htmlFor`/`id`-Verdrahtung, die hier von Hand stand, ist mit in den Baustein
          gewandert und gilt dort jetzt für alle fünf Filterleisten.
          Beide Auswahllisten sind seitenspezifisch (Rolle, Konto-Zustand) und stehen deshalb hier
          statt in `filterFelder.ts`. Die beiden `as`-Zusicherungen, die hier standen, sind weg:
          `filterAuswahl` leitet den Wert-Typ aus den Optionen ab. */}
      <Filterleiste
        suche={sucheEingabe}
        onSuche={setSucheEingabe}
        platzhalter="Name, Benutzername, E-Mail..."
        auswahlen={[
          filterAuswahl({
            beschriftung: 'Rolle',
            wert: filters.role,
            onChange: (wert) => handleFilterChange('role', wert),
            optionen: ROLLEN_OPTIONEN,
          }),
          filterAuswahl({
            beschriftung: 'Status',
            wert: filters.status,
            onChange: (wert) => handleFilterChange('status', wert),
            optionen: STATUS_OPTIONEN,
          }),
        ]}
      />

      {/* Liste */}
      {isLoading && !hatGeladen ? (
        <div className="text-center py-12">
          <div className="loading-spinner mx-auto w-8 h-8"></div>
          <p className="mt-4 text-secondary-600">Lade Benutzer...</p>
        </div>
      ) : loadError ? (
        <div className="card p-6">
          <div className="bg-error-50 border border-error-200 rounded-md p-4">
            <p className="text-sm text-error-600">{loadError}</p>
          </div>
          <button onClick={() => void ladeBenutzer()} className="btn btn-outline mt-4">
            Erneut versuchen
          </button>
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 card">
          <UsersIcon className="mx-auto h-12 w-12 text-secondary-400" />
          <p className="mt-4 text-secondary-600">Keine Benutzer gefunden</p>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          {/* Kopf- und Zellenmaße kommen aus `.table` (index.css), nicht aus einer Klassenkette.
              Beim Nachladen bleibt die Tabelle STEHEN und wird nur gedämpft: würde sie durch den
              Spinner ersetzt, verschwände der gerade bediente Knopf aus dem DOM und der
              Tastatur-Fokus fiele auf `<body>` — nach jedem Rollenwechsel neu. `aria-busy` sagt
              dasselbe für Screenreader, die die Dämpfung nicht sehen. Bewusst NICHT `disabled`:
              das würde den fokussierten Knopf ebenfalls aus dem Fokus werfen. */}
          <table
            className={`table transition-opacity ${isLoading ? 'opacity-60' : ''}`}
            aria-busy={isLoading}
          >
            <thead>
              <tr>
                <th>Benutzer</th>
                <th>E-Mail</th>
                <th>Rolle</th>
                <th>Status</th>
                <th>Registriert</th>
                <th>Zuletzt angemeldet</th>
                <th>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                // ⚠️ Das eigene Konto bleibt gesperrt, und zwar aus einem harten Grund: die
                // Admin-Endpunkte verlangen `role === 'ADMIN'`, und die Anmeldung weist inaktive
                // Konten ab. Wer sich selbst herabstuft oder abschaltet, sperrt sich aus genau
                // der Seite aus, die den Schritt zurücknehmen könnte. Wie der Vergleich erkennt,
                // welche Zeile die eigene ist — und warum er zwei Schlüssel prüft: siehe
                // `istEigeneZeile` oben.
                const istEigenesKonto = istEigeneZeile(angemeldet, u);
                const gesperrt = istEigenesKonto || busyId === u.id;
                const sperrGrund = istEigenesKonto ? 'Das eigene Konto lässt sich hier nicht ändern' : undefined;

                return (
                  <tr key={u.id}>
                    <td>
                      <div className="text-sm font-medium text-secondary-900">
                        {anzeigeName(u)}
                        {istEigenesKonto && (
                          <span className="ml-2 text-xs font-normal text-secondary-500">(Sie)</span>
                        )}
                      </div>
                      <div className="text-xs text-secondary-500">@{u.username}</div>
                    </td>
                    <td>
                      <div className="text-sm text-secondary-900">{u.email}</div>
                      {!u.isEmailVerified && (
                        <span className="badge badge-warning">E-Mail unbestätigt</span>
                      )}
                    </td>
                    <td>
                      <select
                        value={u.role}
                        onChange={(e) => void handleRoleChange(u, e.target.value as UserRole)}
                        className="input disabled:bg-secondary-100 disabled:text-secondary-500 disabled:cursor-not-allowed"
                        disabled={gesperrt}
                        title={sperrGrund}
                        aria-label={`Rolle von ${anzeigeName(u)}`}
                      >
                        {ROLLEN.map((rolle) => (
                          <option key={rolle} value={rolle}>{ROLLEN_ANZEIGE[rolle]}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {u.isActive
                        ? <span className="badge badge-success">Aktiv</span>
                        : <span className="badge badge-secondary">Inaktiv</span>}
                    </td>
                    <td>
                      <div className="text-sm text-secondary-500">{datumKurz(u.createdAt)}</div>
                    </td>
                    <td>
                      <div className="text-sm text-secondary-500">{datumKurz(u.lastLoginAt)}</div>
                    </td>
                    <td>
                      {/* Der Name gehört ins `aria-label`, wie beim `<select>` daneben: in der
                          Knopfliste eines Screenreaders stünde sonst siebenmal „Deaktivieren"
                          ohne jeden Hinweis, wen es trifft. */}
                      <button
                        type="button"
                        onClick={() => void handleStatusToggle(u)}
                        disabled={gesperrt}
                        title={sperrGrund}
                        aria-label={`${anzeigeName(u)} ${u.isActive ? 'deaktivieren' : 'aktivieren'}`}
                        className="btn btn-outline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {u.isActive ? 'Deaktivieren' : 'Aktivieren'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Seitenblättern — die Bedingung `totalPages > 1` und die Begrenzung auf `1 … letzte`
              stecken jetzt im Baustein. Der Rücksprung aus einer leergewordenen Seite bleibt
              bewusst in `ladeBenutzer`: er hängt am Lade-ERGEBNIS, und diese Leiste steht gar nicht
              im Baum, wenn die Liste leer ist. */}
          <SeitenblaetterFuss
            seite={pagination.page}
            seitenAnzahl={pagination.totalPages}
            gesamt={pagination.total}
            onSeitenwechsel={seitenwechsel(setFilters)}
          />
        </div>
      )}
    </PageLayout>
  );
};

export default UserManagement;
